import colors from "./colors";
import dayjs from "dayjs";

/**
 * Utility for financial and billing calculations across the application.
 */

/**
 * Robustly detects the payment mode by checking multiple possible fields.
 */
export const getPaymentMode = (row) => {
    if (!row) return 'n/a';

    const pMode = (row.paymentMode || '').toLowerCase();
    const bType = (row.bookingPaymentType || row.additionalInfo?.bookingPaymentType || '').toLowerCase();

    // Explicitly check for cash first as it's the most critical for tallying
    if (pMode.includes('cash') || bType.includes('cash')) return 'cash';

    // Check for other specific modes
    if (pMode.includes('wallet')) return 'wallet';
    if (pMode.includes('upi') || bType.includes('upi')) return 'upi';
    if (pMode.includes('whatsapp') || pMode.includes('link')) return 'payment link';
    if (pMode.includes('netbanking')) return 'netbanking';

    // General classification
    if (pMode.includes('online') || bType.includes('online')) return 'online';

    return pMode || bType || 'n/a';
};

/**
 * Calculates detailed billing information from an order object.
 * @param {Object} order - The order or row object containing amount, paidAmount, discount, etc.
 * @returns {Object} Calculated financials including netAmount, pending, and discount values.
 */
export const calculateBilling = (order) => {
    if (!order) return { total: 0, paid: 0, installment: 0, discountValue: 0, netAmount: 0, pending: 0, isFullyPaid: false };

    // Robust total amount extraction
    let total = parseFloat(order?.amount || order?.additionalInfo?.amount || 0);
    // If top level amount is 0, check invoice as last resort (handling 'NaN' string)
    if (total === 0 && order?.invoice?.totalAmount && order.invoice.totalAmount !== 'NaN') {
        total = parseFloat(order.invoice.totalAmount);
    }

    // Distinguish between the total amount paid on the order (cumulative)
    // and the specific amount paid in this transaction entry (installment).
    const paid = parseFloat(rowPaidAmount(order, true) || 0); // Cumulative for Order Status
    const installment = parseFloat(rowPaidAmount(order, false) || 0); // Individual for Ledger/Tally sum

    const discountRate = parseFloat(order?.discount || order?.additionalInfo?.discount || 0);
    const discountType = (order?.discountType || order?.additionalInfo?.discountType || 'Fixed').toLowerCase();

    let discountValue = 0;
    if (discountType === 'percentage') {
        discountValue = (total * discountRate) / 100;
    } else {
        discountValue = discountRate;
    }

    const netAmount = total - discountValue;
    const pending = netAmount - paid;

    // Check payment status field (must be Completed AND amount condition met)
    const paymentStatus = order?.paymentStatus || order?.additionalInfo?.paymentStatus || '';
    const isStatusCompleted = paymentStatus.toLowerCase() === 'completed';

    return {
        total,
        paid,
        installment,
        discountRate,
        discountType,
        discountValue,
        netAmount,
        pending: Math.max(0, pending), // Ensure zero if paid more (e.g. overpayment)
        isFullyPaid: isStatusCompleted && pending <= 1, // Both status and amount must confirm full payment
        paymentMode: getPaymentMode(order)
    };
};

/**
 * Helper to get the correct paid amount field.
 * @param {Object} row
 * @param {boolean} cumulative - If true, returns the total paid so far. If false, returns the specific installment.
 */
export const rowPaidAmount = (row, cumulative = true) => {
    if (!row) return 0;

    if (cumulative) {
        // Return the highest known paid value (cumulative total)
        const values = [
            row?.updatedPaidAmount,
            row?.additionalInfo?.updatedPaidAmount,
            row?.invoice?.paidAmount
        ].map(v => {
            const parsed = parseFloat(v);
            return isNaN(parsed) ? 0 : parsed;
        });

        // If no cumulative field has value, fallback to the current paidAmount
        const maxCumulative = Math.max(0, ...values);
        if (maxCumulative > 0) return maxCumulative;
    }

    // Return the specific installment/current payment amount (not cumulative)
    // We prioritize the root-level paidAmount as requested ("paidAmount current paid amount")
    if (row?.paidAmount !== undefined && row?.paidAmount !== null && row?.paidAmount !== "") {
        const rootPaid = parseFloat(row.paidAmount);
        if (!isNaN(rootPaid)) return rootPaid;
    }

    // Fallbacks for older data or different structures
    const installmentValues = [
        row?.additionalInfo?.paidAmount,
        row?.cashAmount,
        row?.cardAmount,
        row?.onlineAmount
    ].map(v => {
        const parsed = parseFloat(v);
        return isNaN(parsed) ? 0 : parsed;
    });

    return Math.max(0, ...installmentValues);
};

/**
 * Formats a number as INR currency.
 */
export const formatCurrency = (amount, includeSymbol = true) => {
    const formatted = new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(amount || 0);

    return includeSymbol ? `₹${formatted}` : formatted;
};

/**
 * Maps a paid percentage to specific semantic color tokens.
 * @param {number} percentage - The percentage paid (0-100).
 * @returns {Object} Color palette { color, bg, border, label }.
 */
export const getPaymentProgressColors = (percentage) => {
    const { ranges } = colors.payment;
    if (percentage >= 100) return ranges.completed;
    if (percentage >= 76) return ranges.substantial;
    if (percentage >= 51) return ranges.partial;
    if (percentage >= 26) return ranges.low;
    return ranges.critical;
};

/**
 * Detects special payment states like split, overdue, or pending.
 * @param {Object} row - The order/transaction object.
 * @returns {Object|null} Color palette or null if none match.
 */
export const getPaymentStateColors = (row) => {
    const { states } = colors.payment;

    // 1. Check for Split Payment (multiple types or specific split flag)
    const isSplit = row?.isSplit || row?.additionalInfo?.isSplit || !!(row?.cashAmount && row?.onlineAmount);
    if (isSplit) return states.split;

    // 2. Check for Overdue (based on end date and pending amount)
    const isOverdue = row?.additionalInfo?.endDate &&
        dayjs(row.additionalInfo.endDate).isBefore(dayjs()) &&
        calculateBilling(row).pending > 0;
    if (isOverdue) return states.overdue;

    // 3. Fallback to pending if no payment detected at all
    if (calculateBilling(row).paid === 0) return states.pending;

    return null;
};
